import { computed, inject, Injectable, signal } from '@angular/core';
import { PermissionKey, UserRole } from '../models';
import { RoleService } from './role.service';
import { AuthStore } from '../auth/auth.store';

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly authStore = inject(AuthStore);
  private readonly roleService = inject(RoleService);

  private readonly _initialized = signal(false);

  readonly initialized = this._initialized.asReadonly();

  readonly effectivePermissions = computed(() => {
    const roles = this.roleService.roles();
    const user = this.authStore.user();
    if (!user || roles.length === 0) return [] as PermissionKey[];

    const roleId = user.roleId ?? this.roleService.getRoleIdForBaseRole(user.role);
    const role = roles.find((item) => item.id === roleId);
    return role?.permissions ?? [];
  });

  async ensureInitialized(): Promise<void> {
    if (this._initialized()) return;
    await this.roleService.loadRoles();
    this._initialized.set(true);
  }

  hasPermission(permission: PermissionKey): boolean {
    return this.effectivePermissions().includes(permission);
  }

  hasAnyPermission(permissions: PermissionKey[]): boolean {
    return permissions.some((permission) => this.hasPermission(permission));
  }

  getBaseRoleLabel(role: UserRole | null): string {
    if (!role) return 'Unknown';
    return role.replace('_', ' ');
  }
}
